import { useMemo } from 'react';

import useConversationsQuery from '../../hooks/data-fetching/useConversationsQuery';
import useHasPermission from '../../hooks/useHasPermission';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { groupByKey, calculateTextingStatus } from '../../utils/texting';

type TextingCounts = {
  NEEDS_HELP?: number,
  NEW: number,
  UNRESOLVED: number,
  RESOLVED: number,
  TOTAL: number,
};

type Position = {
  affordableWaitlist: {
    application: { twoWayRecipientIds?: Array<string> },
  },
};

const EMPTY_TEXTING: TextingCounts = {
  NEW: 0,
  UNRESOLVED: 0,
  RESOLVED: 0,
  TOTAL: 0,
};

const getRecipientIds = (position: Position): Array<string> =>
  position.affordableWaitlist.application.twoWayRecipientIds ?? [];

/**
 * Attaches each position's texting counts as `texting`, so the Texting column
 * can render and be filtered through PROPERTY_PATH_MAP like any other column.
 * Mirrors the texting half of ManageAffordableWaitlist/hooks.useParseResults.
 */
const useTexting = ({
  positions,
  leslieEnabled,
}: {
  positions: Array<Position>,
  leslieEnabled: boolean,
}): Array<{ ...Position, texting: TextingCounts }> => {
  const { isTwoWayCommunicationActive } = useSelectedProperty();
  const hasCommunicationCreatePermission = useHasPermission(
    'communication-create',
  );
  const canFetchConversations = Boolean(
    isTwoWayCommunicationActive && hasCommunicationCreatePermission,
  );

  const recipientIds = useMemo(
    () => positions.flatMap(getRecipientIds),
    [positions],
  );

  const { conversations } = useConversationsQuery({
    recipientIds,
    enabled: canFetchConversations,
  });

  const conversationsByRecipientId = useMemo(
    () => groupByKey(conversations, (c) => c.recipient.customerId),
    [conversations],
  );

  return useMemo(
    () =>
      positions.map((position) => {
        const positionRecipientIds = getRecipientIds(position);
        return {
          ...position,
          texting: positionRecipientIds.length
            ? calculateTextingStatus(
                positionRecipientIds,
                conversationsByRecipientId,
                leslieEnabled,
              )
            : EMPTY_TEXTING,
        };
      }),
    [positions, conversationsByRecipientId, leslieEnabled],
  );
};

export default useTexting;
